import React, { useState, useEffect } from 'react';
import { FaTimes, FaComments, FaPlus, FaEnvelope, FaPhone, FaUsers, FaCalendar, FaEye, FaTag, FaLink, FaPaperPlane, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaProjectDiagram, FaDownload } from 'react-icons/fa';
import apiClient from '../../apiClient';
import './StakeholderCommunications.css';

function StakeholderCommunications({ isOpen, stakeholder, onClose, apiBaseUrl }) {
  const [communications, setCommunications] = useState([]);
  const [keyMessages, setKeyMessages] = useState([]);
  const [keyMessageThemes, setKeyMessageThemes] = useState([]);
  const [stakeholderGroups, setStakeholderGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCommForm, setShowNewCommForm] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    theme: '',
    group: '',
    status: '',
    source: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newCommunication, setNewCommunication] = useState({
    communication_type: 'email',
    subject: '',
    content: '',
    key_message_ids: [],
    theme_id: '',
    group_id: '',
    scheduled_date: '',
    status: 'draft',
    notes: ''
  });

  // Fetch data
  useEffect(() => {
    if (isOpen && stakeholder) {
      fetchStakeholderCommunications();
      fetchKeyMessages();
      fetchKeyMessageThemes();
      fetchStakeholderGroups();
    }
  }, [isOpen, stakeholder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStakeholderCommunications = async () => {
    try {
      const response = await apiClient.get(`/v2/stakeholders/${stakeholder.id}/communications`);
      // The v2 endpoint returns paginated data
      if (response.data.communications) {
        setCommunications(response.data.communications);
      } else if (Array.isArray(response.data)) {
        // Fallback for non-paginated response
        setCommunications(response.data);
      } else {
        setCommunications([]);
      }
    } catch (err) {
      console.error('Error fetching stakeholder communications:', err);
      setCommunications([]);
    }
  };

  const fetchKeyMessages = async () => {
    try {
      const response = await apiClient.get('/key-messages');
      setKeyMessages(response.data || []);
    } catch (err) {
      console.error('Error fetching key messages:', err);
    }
  };

  const fetchKeyMessageThemes = async () => {
    try {
      const response = await apiClient.get('/key-message-themes');
      setKeyMessageThemes(response.data || []);
    } catch (err) {
      console.error('Error fetching themes:', err);
    }
  };

  const fetchStakeholderGroups = async () => {
    try {
      const response = await apiClient.get('/stakeholder_groups');
      setStakeholderGroups(response.data || []);
    } catch (err) {
      console.error('Error fetching stakeholder groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommunication = async (e) => {
    e.preventDefault();
    try {
      const communicationData = {
        ...newCommunication,
        stakeholder_id: stakeholder.id,
        key_message_ids: newCommunication.key_message_ids.length > 0 ? newCommunication.key_message_ids : null,
        theme_id: newCommunication.theme_id || null,
        group_id: newCommunication.group_id || null,
        scheduled_date: newCommunication.scheduled_date || null
      };

      await apiClient.post(`/v2/stakeholders/${stakeholder.id}/communications`, communicationData);
      
      // Reset form and refresh data
      setNewCommunication({
        communication_type: 'email',
        subject: '',
        content: '',
        key_message_ids: [],
        theme_id: '',
        group_id: '',
        scheduled_date: '',
        status: 'draft',
        notes: ''
      });
      setShowNewCommForm(false);
      fetchStakeholderCommunications();
    } catch (err) {
      console.error('Error creating communication:', err);
    }
  };

  const handleUpdateCommunicationStatus = async (commId, newStatus) => {
    try {
      await apiClient.patch(`/v2/stakeholders/${stakeholder.id}/communications/${commId}`, {
        status: newStatus
      });
      fetchStakeholderCommunications();
    } catch (err) {
      console.error('Error updating communication status:', err);
    }
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const headers = ['Date', 'Type', 'Subject', 'Content', 'Status', 'Source', 'Theme', 'Key Messages'];
    const rows = filteredCommunications.map(comm => [
      new Date(comm.created_at).toLocaleDateString(),
      comm.communication_type,
      comm.subject,
      comm.content || '',
      comm.status,
      comm.source || 'manual',
      comm.theme_name || '',
      comm.key_messages?.map(msg => msg.title).join('; ') || ''
    ]);

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${stakeholder.full_name}_communications_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRelevantKeyMessages = () => {
    if (!stakeholder || !keyMessages.length) return [];
    
    // Filter key messages based on stakeholder profile
    return keyMessages.filter(msg => {
      // Match by audience, stakeholder type, or influence level
      const audienceMatch = !msg.target_audience || 
        msg.target_audience.includes(stakeholder.stakeholder_category) ||
        msg.target_audience.includes(stakeholder.stakeholder_type);
      
      const influenceMatch = !msg.influence_level || 
        msg.influence_level === stakeholder.influence_level;
        
      return audienceMatch || influenceMatch;
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <FaPaperPlane className="status-icon sent" />;
      case 'delivered': return <FaCheckCircle className="status-icon delivered" />;
      case 'read': return <FaEye className="status-icon read" />;
      case 'responded': return <FaComments className="status-icon responded" />;
      case 'scheduled': return <FaCalendar className="status-icon scheduled" />;
      case 'failed': return <FaExclamationTriangle className="status-icon failed" />;
      case 'draft': return <FaFileAlt className="status-icon draft" />;
      default: return <FaComments className="status-icon" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <FaEnvelope />;
      case 'phone': return <FaPhone />;
      case 'meeting': return <FaUsers />;
      case 'newsletter': return <FaEnvelope />;
      default: return <FaComments />;
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'event': return <FaProjectDiagram title="From Communication Plan" />;
      case 'plan': return <FaProjectDiagram title="From Communication Plan" />;
      case 'assessment': return <FaFileAlt title="From Assessment" />;
      case 'manual': 
      default: 
        return null;
    }
  };

  const filteredCommunications = communications.filter(comm => {
    // Apply filters
    const matchesFilters = (!filters.type || comm.communication_type === filters.type) &&
           (!filters.theme || comm.theme_id === parseInt(filters.theme)) &&
           (!filters.group || comm.group_id === parseInt(filters.group)) &&
           (!filters.status || comm.status === filters.status) &&
           (!filters.source || comm.source === filters.source);
    
    // Apply search
    const matchesSearch = !searchTerm || 
      comm.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.theme_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.key_messages?.some(msg => msg.title?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilters && matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content communications-modal">
        <div className="modal-header">
          <h3>
            <FaComments className="modal-icon" />
            Communications: {stakeholder.display_name || stakeholder.full_name}
          </h3>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">Loading communications...</div>
          ) : (
            <>
              {/* Quick Stats */}
              <div className="comm-stats">
                <div className="stat-card">
                  <span className="stat-number">{communications.length}</span>
                  <span className="stat-label">Total Communications</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">
                    {communications.filter(c => c.status === 'sent' || c.status === 'delivered').length}
                  </span>
                  <span className="stat-label">Sent</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">
                    {communications.filter(c => c.status === 'responded').length}
                  </span>
                  <span className="stat-label">Responses</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">
                    {communications.filter(c => c.status === 'scheduled').length}
                  </span>
                  <span className="stat-label">Scheduled</span>
                </div>
              </div>

              {/* Action Bar */}
              <div className="action-bar">
                <button 
                  className="btn primary"
                  onClick={() => setShowNewCommForm(true)}
                >
                  <FaPlus /> New Communication
                </button>

                {filteredCommunications.length > 0 && (
                  <button
                    className="btn secondary"
                    onClick={exportToCSV}
                    title="Export to CSV"
                  >
                    <FaDownload /> Export
                  </button>
                )}

                <div className="filters">
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="">All Types</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="meeting">Meeting</option>
                    <option value="newsletter">Newsletter</option>
                  </select>

                  <select
                    value={filters.theme}
                    onChange={(e) => setFilters(prev => ({ ...prev, theme: e.target.value }))}
                  >
                    <option value="">All Themes</option>
                    {keyMessageThemes.map(theme => (
                      <option key={theme.id} value={theme.id}>{theme.name}</option>
                    ))}
                  </select>

                  <select
                    value={filters.group}
                    onChange={(e) => setFilters(prev => ({ ...prev, group: e.target.value }))}
                  >
                    <option value="">All Groups</option>
                    {stakeholderGroups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>

                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="sent">Sent</option>
                    <option value="delivered">Delivered</option>
                    <option value="read">Read</option>
                    <option value="responded">Responded</option>
                    <option value="failed">Failed</option>
                  </select>

                  <select
                    value={filters.source}
                    onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                  >
                    <option value="">All Sources</option>
                    <option value="manual">Manual</option>
                    <option value="event">Event</option>
                    <option value="plan">Plan</option>
                    <option value="assessment">Assessment</option>
                  </select>

                  <input
                    type="text"
                    placeholder="Search communications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* New Communication Form */}
              {showNewCommForm && (
                <div className="new-comm-form">
                  <h4>New Communication</h4>
                  <form onSubmit={handleCreateCommunication}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Type:</label>
                        <select
                          value={newCommunication.communication_type}
                          onChange={(e) => setNewCommunication(prev => ({ 
                            ...prev, 
                            communication_type: e.target.value 
                          }))}
                          required
                        >
                          <option value="email">Email</option>
                          <option value="phone">Phone Call</option>
                          <option value="meeting">Meeting</option>
                          <option value="newsletter">Newsletter</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Status:</label>
                        <select
                          value={newCommunication.status}
                          onChange={(e) => setNewCommunication(prev => ({ 
                            ...prev, 
                            status: e.target.value 
                          }))}
                        >
                          <option value="draft">Draft</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="sent">Sent</option>
                        </select>
                      </div>

                      {newCommunication.status === 'scheduled' && (
                        <div className="form-group">
                          <label>Scheduled Date:</label>
                          <input
                            type="datetime-local"
                            value={newCommunication.scheduled_date}
                            onChange={(e) => setNewCommunication(prev => ({ 
                              ...prev, 
                              scheduled_date: e.target.value 
                            }))}
                          />
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Subject/Title:</label>
                      <input
                        type="text"
                        value={newCommunication.subject}
                        onChange={(e) => setNewCommunication(prev => ({ 
                          ...prev, 
                          subject: e.target.value 
                        }))}
                        required
                        placeholder="Communication subject or title"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Link to Theme:</label>
                        <select
                          value={newCommunication.theme_id}
                          onChange={(e) => setNewCommunication(prev => ({ 
                            ...prev, 
                            theme_id: e.target.value 
                          }))}
                        >
                          <option value="">Select theme (optional)</option>
                          {keyMessageThemes.map(theme => (
                            <option key={theme.id} value={theme.id}>{theme.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Link to Stakeholder Group:</label>
                        <select
                          value={newCommunication.group_id}
                          onChange={(e) => setNewCommunication(prev => ({ 
                            ...prev, 
                            group_id: e.target.value 
                          }))}
                        >
                          <option value="">Select group (optional)</option>
                          {stakeholderGroups.map(group => (
                            <option key={group.id} value={group.id}>{group.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Link Key Messages:</label>
                      <div className="key-messages-selector">
                        {getRelevantKeyMessages().map(msg => (
                          <label key={msg.id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={newCommunication.key_message_ids.includes(msg.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewCommunication(prev => ({
                                    ...prev,
                                    key_message_ids: [...prev.key_message_ids, msg.id]
                                  }));
                                } else {
                                  setNewCommunication(prev => ({
                                    ...prev,
                                    key_message_ids: prev.key_message_ids.filter(id => id !== msg.id)
                                  }));
                                }
                              }}
                            />
                            <span className="checkbox-text">
                              {msg.title}
                              {msg.theme_name && <small> ({msg.theme_name})</small>}
                            </span>
                          </label>
                        ))}
                        {getRelevantKeyMessages().length === 0 && (
                          <p className="no-messages">No relevant key messages found for this stakeholder profile.</p>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Content/Notes:</label>
                      <textarea
                        value={newCommunication.content}
                        onChange={(e) => setNewCommunication(prev => ({ 
                          ...prev, 
                          content: e.target.value 
                        }))}
                        rows="4"
                        placeholder="Communication content, meeting notes, or additional details..."
                      />
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn primary">
                        Create Communication
                      </button>
                      <button 
                        type="button" 
                        className="btn secondary"
                        onClick={() => setShowNewCommForm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Communications List */}
              <div className="communications-list">
                {filteredCommunications.length === 0 ? (
                  <div className="empty-state">
                    <FaComments className="empty-icon" />
                    <h4>No communications yet</h4>
                    <p>Start building a communication history with this stakeholder.</p>
                    <button 
                      className="btn primary"
                      onClick={() => setShowNewCommForm(true)}
                    >
                      <FaPlus /> Create First Communication
                    </button>
                  </div>
                ) : (
                  filteredCommunications.map(comm => (
                    <div key={comm.id} className="communication-item">
                      <div className="comm-header">
                        <div className="comm-title-section">
                          <div className="comm-type-icon">
                            {getTypeIcon(comm.communication_type)}
                          </div>
                          <div className="comm-info">
                            <h5>
                              {comm.subject}
                              {comm.source && comm.source !== 'manual' && (
                                <span className="comm-source-icon">
                                  {getSourceIcon(comm.source)}
                                </span>
                              )}
                            </h5>
                            <div className="comm-meta">
                              <span className="comm-type">{comm.communication_type}</span>
                              {comm.theme_name && (
                                <span className="comm-theme">
                                  <FaTag /> {comm.theme_name}
                                </span>
                              )}
                              {comm.plan_name && (
                                <span className="comm-plan">
                                  <FaLink /> {comm.plan_name}
                                </span>
                              )}
                              {comm.event && (
                                <span className="comm-event" title={`Event: ${comm.event.name}${comm.event.plan_name ? ` (${comm.event.plan_name})` : ''}`}>
                                  <FaProjectDiagram /> {comm.event.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="comm-status-section">
                          <div className="comm-status">
                            {getStatusIcon(comm.status)}
                            <span>{comm.status}</span>
                          </div>
                          <div className="comm-date">
                            {new Date(comm.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {comm.content && (
                        <div className="comm-content">
                          {comm.content}
                        </div>
                      )}

                      {/* Event Details for Event-originated Communications */}
                      {comm.event && (
                        <div className="comm-event-details">
                          <strong>Event Details:</strong>
                          <div className="event-info">
                            <p><strong>Event:</strong> {comm.event.name}</p>
                            {comm.event.plan_name && <p><strong>Communication Plan:</strong> {comm.event.plan_name}</p>}
                            {comm.event.event_date && <p><strong>Event Date:</strong> {new Date(comm.event.event_date).toLocaleDateString()}</p>}
                            {comm.event.description && <p><strong>Description:</strong> {comm.event.description}</p>}
                          </div>
                        </div>
                      )}

                      {comm.key_messages && comm.key_messages.length > 0 && (
                        <div className="comm-key-messages">
                          <strong>Linked Key Messages:</strong>
                          <ul>
                            {comm.key_messages.map(msg => (
                              <li key={msg.id}>{msg.title}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="comm-actions">
                        {comm.status === 'draft' && (
                          <button 
                            className="btn small primary"
                            onClick={() => handleUpdateCommunicationStatus(comm.id, 'sent')}
                          >
                            Mark as Sent
                          </button>
                        )}
                        {comm.status === 'sent' && (
                          <button 
                            className="btn small success"
                            onClick={() => handleUpdateCommunicationStatus(comm.id, 'responded')}
                          >
                            Mark as Responded
                          </button>
                        )}
                        {comm.status === 'scheduled' && (
                          <span className="scheduled-info">
                            Scheduled for: {new Date(comm.scheduled_date).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StakeholderCommunications; 